// ─── cTrader Gateway Client (stream-snapshot reads only) ───────
// Reads cTrader account + positions data exclusively from the shared SSE
// stream's in-memory snapshot. The stream reconciles positions, trader
// (balance/equity/margin) and symbols ON its single persistent cTrader
// WebSocket and pushes them as SSE events, which `shared-ctrader-stream`
// caches per account.
//
// There is no separate "get-account-data" gateway call anymore — that would
// open a second cTrader WebSocket and risk kicking the live stream session.
// Every consumer (trailing-stop, Chin risk control, OrderPanel, dashboard)
// reads the same snapshot from the same single connection.
//
// When no snapshot exists yet (stream not started, or first reconcile still
// pending), this returns null; callers treat that as "not ready yet" and
// retry on their next poll / stream event.

import { getLatestPositionsSnapshot, getLatestAccountSnapshot } from '@/lib/shared-ctrader-stream';

export interface GetAccountDataResult {
  success: boolean;
  accountId: number;
  appAuthOk?: boolean;
  accountAuth?: Record<string, unknown> | null;
  traderRes: Record<string, unknown> | null;
  reconcileRes: Record<string, unknown> | null;
  symbolRes: Record<string, unknown> | null;
  marginEvent?: Record<string, unknown> | null;
  allMessages?: Array<{ payloadType: number; payload: Record<string, unknown> }>;
  log?: string;
  error?: string;
}

/**
 * Read the latest trader/margin + positions snapshot from the shared stream.
 * Returns null when the stream hasn't pushed a snapshot for this account yet.
 */
export function getAccountData(accountId: number): Promise<GetAccountDataResult | null> {
  const accSnap = getLatestAccountSnapshot(accountId);
  const posSnap = getLatestPositionsSnapshot(accountId);

  if (!accSnap?.traderRes || !posSnap) return Promise.resolve(null);

  return Promise.resolve({
    success: true,
    accountId,
    traderRes: accSnap.traderRes,
    reconcileRes: posSnap.reconcileRes,
    symbolRes: posSnap.symbolRes ?? null,
    marginEvent: accSnap.marginEvent ?? null,
    allMessages: [],
    log: 'stream-snapshot',
  });
}